/**
 * V6.3 — Admin Brokers (Invitation Only)
 * Réservé aux users avec is_admin=true. Le backend impose déjà la restriction
 * (route /api/admin/brokers → 403 admin_only). Ici on filtre juste l'UI.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { AdminMigrations } from "@/components/admin/AdminMigrations";
import {
  UserPlus, CheckCircle, XCircle, Prohibit, ShieldCheck, Trash, PencilSimple,
  MagnifyingGlass, WarningCircle,
} from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(iso));
  } catch { return "—"; }
}
function fmtDateTime(iso) {
  if (!iso) return "Jamais";
  try {
    return new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
  } catch { return "—"; }
}

export default function AdminBrokers() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [brokers, setBrokers] = useState([]);
  const [q, setQ] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || !user.is_admin)) {
      navigate("/", { replace: true });
    }
  }, [user, authLoading, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/brokers");
      setBrokers(r.data || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Réservé aux administrateurs.");
        navigate("/", { replace: true });
      } else {
        toast.error("Impossible de charger la liste des courtiers.");
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (user?.is_admin) load(); /* eslint-disable-next-line */ }, [user?.is_admin]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return brokers;
    return brokers.filter((b) =>
      (b.email || "").toLowerCase().includes(t) ||
      (b.name || "").toLowerCase().includes(t) ||
      (b.slug || "").toLowerCase().includes(t)
    );
  }, [brokers, q]);

  const toggleActive = async (b) => {
    const next = !b.active;
    if (!next && b.user_id === user.user_id) {
      toast.error("Vous ne pouvez pas vous désactiver vous-même.");
      return;
    }
    if (!next && !window.confirm(`Désactiver l'accès de ${b.email} ? Toutes ses sessions actives seront révoquées.`)) return;
    try {
      const r = await api.patch(`/admin/brokers/${b.user_id}`, { active: next });
      setBrokers((arr) => arr.map((x) => (x.user_id === b.user_id ? r.data : x)));
      toast.success(next ? "Accès activé" : "Accès désactivé (sessions purgées)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const invite = async ({ email, name, slug }) => {
    try {
      const r = await api.post("/admin/brokers", { email, name, slug });
      setBrokers((arr) => [r.data, ...arr]);
      toast.success(`✅ ${email} invité`);
      setInviteOpen(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const MAP = {
        email_already_invited: "Cet email est déjà invité.",
        slug_already_used: "Ce slug est déjà pris.",
        invalid_email: "Email invalide.",
        invalid_slug: "Slug invalide.",
        admin_only: "Réservé admin.",
      };
      toast.error(MAP[d] || "Erreur — invitation");
      throw e;
    }
  };

  if (authLoading || !user) return null;
  if (!user.is_admin) return null;

  return (
    <Layout title="Courtiers autorisés" subtitle="Invitation only" backTo="/">
      <div className="max-w-5xl mx-auto space-y-5" data-testid="admin-brokers">

        <div className="rounded-2xl p-5 border-2 border-blue-100 bg-gradient-to-br from-blue-50 to-white">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={18} weight="fill" className="text-blue-600" />
            <div className="text-[11px] uppercase tracking-widest font-extrabold text-blue-700">Whitelist Crédit Flow</div>
          </div>
          <div className="text-[13px] text-slate-700 leading-relaxed">
            Aucun compte Google inconnu ne peut se connecter. Chaque courtier doit être invité ci-dessous.
            La désactivation coupe l'accès immédiatement et révoque les sessions actives.
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filtrer par email, nom ou slug…"
              className="w-full pl-9 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 outline-none text-[14px] font-bold text-slate-900"
              data-testid="admin-brokers-search"
            />
          </div>
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-slate-900 text-white font-extrabold text-[13px] active:scale-95 transition shadow-md whitespace-nowrap"
            data-testid="admin-invite-btn"
          >
            <UserPlus size={16} weight="bold" /> Inviter un courtier
          </button>
        </div>

        {loading ? (
          <div className="text-slate-400 text-sm animate-pulse py-12 text-center">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl bg-white border border-slate-200 py-12 text-center text-sm text-slate-500">
            Aucun courtier {q ? "ne correspond à ce filtre" : "invité pour le moment"}.
          </div>
        ) : (
          <div className="space-y-2" data-testid="admin-brokers-list">
            {filtered.map((b) => (
              <BrokerRow key={b.user_id} broker={b} onToggle={toggleActive} isSelf={b.user_id === user.user_id} onEdit={load} />
            ))}
          </div>
        )}

        <AdminMigrations />
      </div>

      {inviteOpen && <InviteModal onClose={() => setInviteOpen(false)} onInvite={invite} />}
    </Layout>
  );
}

function BrokerRow({ broker, onToggle, isSelf, onEdit }) {
  const [editing, setEditing] = useState(false);
  return (
    <div
      className={`rounded-2xl border-2 p-4 bg-white transition ${broker.active ? "border-slate-200" : "border-red-200 bg-red-50/40"}`}
      data-testid={`broker-row-${broker.user_id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-extrabold text-sm shrink-0"
             style={{ background: broker.is_admin ? "linear-gradient(135deg,var(--cf-accent),var(--cf-accent-hover))" : "linear-gradient(135deg,var(--cf-primary),var(--cf-primary-hover))" }}>
          {(broker.name || broker.email || "?").trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="font-extrabold text-slate-900 text-[15px] truncate" data-testid="broker-name">{broker.name || "—"}</div>
            {broker.is_admin && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-extrabold uppercase tracking-widest">
                <ShieldCheck size={10} weight="fill" /> Admin
              </span>
            )}
            {isSelf && (
              <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-extrabold uppercase tracking-widest">Vous</span>
            )}
            {broker.active ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-extrabold uppercase tracking-widest" data-testid="broker-status">
                <CheckCircle size={10} weight="fill" /> Actif
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-extrabold uppercase tracking-widest" data-testid="broker-status">
                <XCircle size={10} weight="fill" /> Désactivé
              </span>
            )}
          </div>
          <div className="text-[13px] text-slate-600 mt-0.5 truncate" data-testid="broker-email">{broker.email}</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-slate-500">
            <span><b className="text-slate-700">Slug :</b> {broker.slug || "—"}</span>
            <span><b className="text-slate-700">Créé :</b> {fmtDate(broker.created_at)}</span>
            <span><b className="text-slate-700">Dernière connexion :</b> {fmtDateTime(broker.last_login_at)}</span>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-white border-2 border-slate-200 text-slate-700 font-bold text-[12px] active:scale-95 transition"
            data-testid={`broker-edit-btn-${broker.user_id}`}
          >
            <PencilSimple size={12} weight="bold" /> Éditer
          </button>
          {!isSelf && (
            <button
              type="button"
              onClick={() => onToggle(broker)}
              className={`inline-flex items-center gap-1 px-3 py-2 rounded-full font-bold text-[12px] active:scale-95 transition ${
                broker.active
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-emerald-600 text-white hover:bg-emerald-700"
              }`}
              data-testid={`broker-toggle-btn-${broker.user_id}`}
            >
              {broker.active ? (<><Prohibit size={12} weight="bold" /> Désactiver</>)
                             : (<><CheckCircle size={12} weight="bold" /> Réactiver</>)}
            </button>
          )}
        </div>
      </div>
      {editing && <EditBrokerModal broker={broker} onClose={() => { setEditing(false); onEdit(); }} />}
    </div>
  );
}

function InviteModal({ onClose, onInvite }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await onInvite({ email: email.trim(), name: name.trim() || null, slug: slug.trim() || null });
    } catch { /* toast déjà affiché */ }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50" data-testid="invite-modal" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-4">
          <UserPlus size={20} weight="fill" className="text-blue-600" />
          <div className="font-display text-xl font-extrabold text-slate-900">Inviter un courtier</div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <FieldLabel label="Email Google">
            <input
              type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="marie@exemple.com"
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 outline-none text-[16px] sm:text-[14px] font-bold text-slate-900"
              autoCapitalize="off" autoCorrect="off" spellCheck={false}
              data-testid="invite-email-input"
            />
          </FieldLabel>
          <FieldLabel label="Nom">
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Marie Dupont"
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 outline-none text-[16px] sm:text-[14px] font-bold text-slate-900"
              data-testid="invite-name-input"
            />
          </FieldLabel>
          <FieldLabel label="Slug simulateur" hint="Lettres/chiffres/tirets. Généré depuis l'email si vide.">
            <input
              type="text" value={slug} onChange={(e) => setSlug(e.target.value)}
              placeholder="marie"
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 outline-none text-[16px] sm:text-[14px] font-bold text-slate-900"
              autoCapitalize="off" autoCorrect="off" spellCheck={false}
              data-testid="invite-slug-input"
            />
          </FieldLabel>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-3 rounded-full text-slate-600 font-bold text-[13px] hover:bg-slate-100" data-testid="invite-cancel">Annuler</button>
            <button
              type="submit"
              disabled={busy || !email.trim()}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white font-extrabold text-[14px] disabled:opacity-40 active:scale-95 transition shadow-md"
              style={{ background: "linear-gradient(135deg,var(--cf-primary),var(--cf-primary-hover))" }}
              data-testid="invite-submit"
            >
              {busy ? "Envoi…" : "Inviter"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditBrokerModal({ broker, onClose }) {
  const [name, setName] = useState(broker.name || "");
  const [slug, setSlug] = useState(broker.slug || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const patch = {};
      if (name !== broker.name) patch.name = name;
      if (slug !== broker.slug) patch.slug = slug;
      if (Object.keys(patch).length === 0) { onClose(); return; }
      await api.patch(`/admin/brokers/${broker.user_id}`, patch);
      toast.success("Courtier mis à jour");
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const MAP = { slug_already_used: "Ce slug est déjà pris.", invalid_slug: "Slug invalide." };
      toast.error(MAP[d] || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50" onClick={onClose} data-testid="edit-modal">
      <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="font-display text-xl font-extrabold text-slate-900 mb-4">Éditer {broker.email}</div>
        <form onSubmit={submit} className="space-y-3">
          <FieldLabel label="Nom">
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 outline-none text-[16px] sm:text-[14px] font-bold text-slate-900" data-testid="edit-name-input" />
          </FieldLabel>
          <FieldLabel label="Slug">
            <input value={slug} onChange={(e) => setSlug(e.target.value)} className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 outline-none text-[16px] sm:text-[14px] font-bold text-slate-900" autoCapitalize="off" autoCorrect="off" spellCheck={false} data-testid="edit-slug-input" />
          </FieldLabel>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-3 rounded-full text-slate-600 font-bold text-[13px] hover:bg-slate-100" data-testid="edit-cancel">Annuler</button>
            <button type="submit" disabled={busy} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white font-extrabold text-[14px] disabled:opacity-40 active:scale-95 transition shadow-md" style={{ background: "linear-gradient(135deg,var(--cf-primary),var(--cf-primary-hover))" }} data-testid="edit-submit">
              {busy ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FieldLabel({ label, hint, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest font-extrabold text-slate-500 mb-1">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-slate-400 mt-1.5 leading-relaxed">{hint}</div>}
    </div>
  );
}
